import React from "react";
import { Marker } from "@react-google-maps/api";
import { useAtomValue, useSetAtom } from "jotai";
import { useSearchParams } from "react-router-dom";

import { Stop } from "../../types";
import PoiInfoWindow from "../layers/infoWindows/PoiInfoWindow";
import { SvgMarkerIcon } from "../../assets/Icons/mapIcons/svgMarker";

import { fetchParkingSpots } from "../../api/parkingSpots";
import { fetchTicketAgents } from "../../api/ticketAgent";
import { fetchTicketMachines } from "../../api/ticketMachines";

import {
  parkingEnabledAtom,
  parkingRequestedNameAtom,
  parkingRequestedLlAtom,
  requestedParkingPoiAtom,
  parkingPoisAtom,
  ticketAgentsEnabledAtom,
  ticketAgentsRequestedNameAtom,
  ticketAgentsRequestedLlAtom,
  ticketAgentsPoisAtom,
  requestedTicketAgentPoiAtom,
  ticketMachinesEnabledAtom,
  ticketMachinesRequestedNameAtom,
  ticketMachinesRequestedLlAtom,
  ticketMachinesPoisAtom,
  requestedTicketMachinePoiAtom,
} from "../../atoms";

type PoiType = "parking" | "ticketAgent" | "ticketMachine";

const ALL_PARKING_TOKEN = "alla-parkeringsplatser";
const ALL_AGENTS_TOKEN = "alla-ombud";
const ALL_MACHINES_TOKEN = "alla-automater";

function formatPoiLl(poi: Stop) {
  return `${poi.lat.toFixed(6)},${poi.lng.toFixed(6)}`;
}

function writePoiParams(
  params: URLSearchParams,
  key: string,
  llKey: string,
  enabled: boolean,
  name: string | null,
  ll: string | null,
  allToken: string
) {
  params.delete(key);
  params.delete(llKey);

  if (!enabled) return;

  params.set(key, name ?? allToken);

  // Skriv bara koordinat när en specifik POI är vald.
  // Vid "visa alla" ska ll-parametern inte ligga kvar.
  if (name && ll) {
    params.set(llKey, ll);
  }
}

export function TransitPoisLayer(props: {
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  mapLoaded: boolean;
  zoomTo: number;
  onDimmedChange?: (dimmed: boolean) => void;
}) {
  const { mapRef, mapLoaded, zoomTo, onDimmedChange } = props;

  const [searchParams, setSearchParams] = useSearchParams();

  // Parking
  const parkingEnabled = useAtomValue(parkingEnabledAtom);
  const setParkingEnabled = useSetAtom(parkingEnabledAtom);

  const requestedParkingName = useAtomValue(parkingRequestedNameAtom);
  const setRequestedParkingName = useSetAtom(parkingRequestedNameAtom);

  const requestedParkingLl = useAtomValue(parkingRequestedLlAtom);
  const setRequestedParkingLl = useSetAtom(parkingRequestedLlAtom);

  const parkingPois = useAtomValue(parkingPoisAtom);
  const setParkingPois = useSetAtom(parkingPoisAtom);

  const requestedParkingPoi = useAtomValue(requestedParkingPoiAtom);

  // Ticket agents
  const ticketAgentsEnabled = useAtomValue(ticketAgentsEnabledAtom);
  const setTicketAgentsEnabled = useSetAtom(ticketAgentsEnabledAtom);

  const requestedTicketAgentName = useAtomValue(ticketAgentsRequestedNameAtom);
  const setRequestedTicketAgentName = useSetAtom(ticketAgentsRequestedNameAtom);

  const requestedTicketAgentLl = useAtomValue(ticketAgentsRequestedLlAtom);
  const setRequestedTicketAgentLl = useSetAtom(ticketAgentsRequestedLlAtom);

  const ticketAgentsPois = useAtomValue(ticketAgentsPoisAtom);
  const setTicketAgentsPois = useSetAtom(ticketAgentsPoisAtom);

  const requestedTicketAgentPoi = useAtomValue(requestedTicketAgentPoiAtom);

  // Ticket machines
  const ticketMachinesEnabled = useAtomValue(ticketMachinesEnabledAtom);
  const setTicketMachinesEnabled = useSetAtom(ticketMachinesEnabledAtom);

  const requestedTicketMachineName = useAtomValue(
    ticketMachinesRequestedNameAtom
  );
  const setRequestedTicketMachineName = useSetAtom(
    ticketMachinesRequestedNameAtom
  );

  const requestedTicketMachineLl = useAtomValue(ticketMachinesRequestedLlAtom);
  const setRequestedTicketMachineLl = useSetAtom(ticketMachinesRequestedLlAtom);

  const ticketMachinesPois = useAtomValue(ticketMachinesPoisAtom);
  const setTicketMachinesPois = useSetAtom(ticketMachinesPoisAtom);

  const requestedTicketMachinePoi = useAtomValue(requestedTicketMachinePoiAtom);

  const [poiInfoWindow, setPoiInfoWindow] = React.useState<{
    type: PoiType;
    poi: Stop & { label?: string; showIcon?: boolean };
  } | null>(null);

  const lastZoomedParkingIdRef = React.useRef<string | null>(null);
  const lastZoomedAgentRef = React.useRef<string | null>(null);
  const lastZoomedMachineRef = React.useRef<string | null>(null);

  const lastFittedParkingAllRef = React.useRef(false);
  const lastFittedAgentsAllRef = React.useRef(false);
  const lastFittedMachinesAllRef = React.useRef(false);

  const hydratedFromUrlRef = React.useRef(false);
  const skipFirstWriteRef = React.useRef(true);

  const dimmed = parkingEnabled || ticketAgentsEnabled || ticketMachinesEnabled;

  React.useEffect(() => {
    onDimmedChange?.(dimmed);
  }, [dimmed, onDimmedChange]);

  const zoomToPoi = React.useCallback(
    (p: Stop, z = zoomTo) => {
      if (!mapRef.current) return;

      mapRef.current.panTo({ lat: p.lat, lng: p.lng });
      mapRef.current.setZoom(z);
    },
    [mapRef, zoomTo]
  );

  const fitBoundsForPois = React.useCallback(
    (pois: Stop[], padding = 60) => {
      if (!mapRef.current) return;
      if (pois.length === 0) return;

      const bounds = new google.maps.LatLngBounds();

      pois.forEach((p) => {
        bounds.extend({ lat: p.lat, lng: p.lng });
      });

      mapRef.current.fitBounds(bounds, padding);
    },
    [mapRef]
  );

  // URL -> state, körs bara initialt.
  React.useEffect(() => {
    const parking = searchParams.get("parkering");
    const parkingLl = searchParams.get("parkeringLl");

    if (parking || parkingLl) {
      setParkingEnabled(true);

      if (parking && parking !== ALL_PARKING_TOKEN) {
        setRequestedParkingName(parking);
        setRequestedParkingLl(parkingLl);
      } else {
        setRequestedParkingName(null);
        setRequestedParkingLl(null);
      }
    }

    const agent = searchParams.get("ombud");
    const agentLl = searchParams.get("ombudLl");

    if (agent || agentLl) {
      setTicketAgentsEnabled(true);

      if (agent && agent !== ALL_AGENTS_TOKEN) {
        setRequestedTicketAgentName(agent);
        setRequestedTicketAgentLl(agentLl);
      } else {
        setRequestedTicketAgentName(null);
        setRequestedTicketAgentLl(null);
      }
    }

    const machine = searchParams.get("automat");
    const machineLl = searchParams.get("automatLl");

    if (machine || machineLl) {
      setTicketMachinesEnabled(true);

      if (machine && machine !== ALL_MACHINES_TOKEN) {
        setRequestedTicketMachineName(machine);
        setRequestedTicketMachineLl(machineLl);
      } else {
        setRequestedTicketMachineName(null);
        setRequestedTicketMachineLl(null);
      }
    }

    hydratedFromUrlRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // state -> URL, samlat för alla POI-parametrar.
  React.useEffect(() => {
    if (!hydratedFromUrlRef.current) return;

    if (skipFirstWriteRef.current) {
      skipFirstWriteRef.current = false;
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        writePoiParams(
          next,
          "parkering",
          "parkeringLl",
          parkingEnabled,
          requestedParkingName,
          requestedParkingLl,
          ALL_PARKING_TOKEN
        );

        writePoiParams(
          next,
          "ombud",
          "ombudLl",
          ticketAgentsEnabled,
          requestedTicketAgentName,
          requestedTicketAgentLl,
          ALL_AGENTS_TOKEN
        );

        writePoiParams(
          next,
          "automat",
          "automatLl",
          ticketMachinesEnabled,
          requestedTicketMachineName,
          requestedTicketMachineLl,
          ALL_MACHINES_TOKEN
        );

        if (next.toString() === prev.toString()) {
          return prev;
        }

        return next;
      },
      { replace: true }
    );
  }, [
    parkingEnabled,
    requestedParkingName,
    requestedParkingLl,
    ticketAgentsEnabled,
    requestedTicketAgentName,
    requestedTicketAgentLl,
    ticketMachinesEnabled,
    requestedTicketMachineName,
    requestedTicketMachineLl,
    setSearchParams,
  ]);

  // Rensa stale ll när läget är "visa alla".
  React.useEffect(() => {
    if (!parkingEnabled) return;
    if (requestedParkingName) return;
    if (!requestedParkingLl) return;

    setRequestedParkingLl(null);
  }, [
    parkingEnabled,
    requestedParkingName,
    requestedParkingLl,
    setRequestedParkingLl,
  ]);

  React.useEffect(() => {
    if (!ticketAgentsEnabled) return;
    if (requestedTicketAgentName) return;
    if (!requestedTicketAgentLl) return;

    setRequestedTicketAgentLl(null);
  }, [
    ticketAgentsEnabled,
    requestedTicketAgentName,
    requestedTicketAgentLl,
    setRequestedTicketAgentLl,
  ]);

  React.useEffect(() => {
    if (!ticketMachinesEnabled) return;
    if (requestedTicketMachineName) return;
    if (!requestedTicketMachineLl) return;

    setRequestedTicketMachineLl(null);
  }, [
    ticketMachinesEnabled,
    requestedTicketMachineName,
    requestedTicketMachineLl,
    setRequestedTicketMachineLl,
  ]);

  const shouldLoadParking =
    parkingEnabled ||
    Boolean(requestedParkingName) ||
    Boolean(requestedParkingLl);

  const shouldLoadAgents =
    ticketAgentsEnabled ||
    Boolean(requestedTicketAgentName) ||
    Boolean(requestedTicketAgentLl);

  const shouldLoadMachines =
    ticketMachinesEnabled ||
    Boolean(requestedTicketMachineName) ||
    Boolean(requestedTicketMachineLl);

  React.useEffect(() => {
    if (!shouldLoadParking) return;
    if (parkingPois.length > 0) return;

    let cancelled = false;

    (async () => {
      try {
        const pois = await fetchParkingSpots();

        if (!cancelled) {
          setParkingPois(pois);
        }
      } catch (error) {
        console.error("Failed to fetch parking spots", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldLoadParking, parkingPois.length, setParkingPois]);

  React.useEffect(() => {
    if (!shouldLoadAgents) return;
    if (ticketAgentsPois.length > 0) return;

    let cancelled = false;

    (async () => {
      try {
        const pois = await fetchTicketAgents();

        if (!cancelled) {
          setTicketAgentsPois(pois);
        }
      } catch (error) {
        console.error("Failed to fetch ticket agents", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldLoadAgents, ticketAgentsPois.length, setTicketAgentsPois]);

  React.useEffect(() => {
    if (!shouldLoadMachines) return;
    if (ticketMachinesPois.length > 0) return;

    let cancelled = false;

    (async () => {
      try {
        const pois = await fetchTicketMachines();

        if (!cancelled) {
          setTicketMachinesPois(pois);
        }
      } catch (error) {
        console.error("Failed to fetch ticket machines", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldLoadMachines, ticketMachinesPois.length, setTicketMachinesPois]);

  // Fit "alla parkeringar".
  React.useEffect(() => {
    if (!mapLoaded) return;

    if (!parkingEnabled) {
      lastFittedParkingAllRef.current = false;
      return;
    }

    if (requestedParkingName || requestedParkingLl) {
      lastFittedParkingAllRef.current = false;
      return;
    }

    if (parkingPois.length === 0) return;
    if (lastFittedParkingAllRef.current) return;

    lastFittedParkingAllRef.current = true;
    fitBoundsForPois(parkingPois);
  }, [
    mapLoaded,
    parkingEnabled,
    requestedParkingName,
    requestedParkingLl,
    parkingPois,
    fitBoundsForPois,
  ]);

  // Fit "alla ombud".
  React.useEffect(() => {
    if (!mapLoaded) return;

    if (!ticketAgentsEnabled) {
      lastFittedAgentsAllRef.current = false;
      return;
    }

    if (requestedTicketAgentName || requestedTicketAgentLl) {
      lastFittedAgentsAllRef.current = false;
      return;
    }

    if (ticketAgentsPois.length === 0) return;
    if (lastFittedAgentsAllRef.current) return;

    lastFittedAgentsAllRef.current = true;
    fitBoundsForPois(ticketAgentsPois);
  }, [
    mapLoaded,
    ticketAgentsEnabled,
    requestedTicketAgentName,
    requestedTicketAgentLl,
    ticketAgentsPois,
    fitBoundsForPois,
  ]);

  // Fit "alla automater".
  React.useEffect(() => {
    if (!mapLoaded) return;

    if (!ticketMachinesEnabled) {
      lastFittedMachinesAllRef.current = false;
      return;
    }

    if (requestedTicketMachineName || requestedTicketMachineLl) {
      lastFittedMachinesAllRef.current = false;
      return;
    }

    if (ticketMachinesPois.length === 0) return;
    if (lastFittedMachinesAllRef.current) return;

    lastFittedMachinesAllRef.current = true;
    fitBoundsForPois(ticketMachinesPois);
  }, [
    mapLoaded,
    ticketMachinesEnabled,
    requestedTicketMachineName,
    requestedTicketMachineLl,
    ticketMachinesPois,
    fitBoundsForPois,
  ]);

  // Öppna specifik parkering från URL/state.
  React.useEffect(() => {
    if (!mapLoaded) return;
    if (
      (!requestedParkingName && !requestedParkingLl) ||
      !requestedParkingPoi
    ) {
      return;
    }
    if (!mapRef.current) return;

    const key = `${requestedParkingName ?? ""}|${requestedParkingLl ?? ""}`;

    if (lastZoomedParkingIdRef.current === key) return;
    lastZoomedParkingIdRef.current = key;

    zoomToPoi(requestedParkingPoi, zoomTo);
    setPoiInfoWindow({ type: "parking", poi: requestedParkingPoi });
  }, [
    mapLoaded,
    requestedParkingName,
    requestedParkingLl,
    requestedParkingPoi,
    mapRef,
    zoomToPoi,
    zoomTo,
  ]);

  // Öppna specifikt ombud från URL/state.
  React.useEffect(() => {
    if (!mapLoaded) return;
    if (
      (!requestedTicketAgentName && !requestedTicketAgentLl) ||
      !requestedTicketAgentPoi
    ) {
      return;
    }
    if (!mapRef.current) return;

    const key = `${requestedTicketAgentName ?? ""}|${
      requestedTicketAgentLl ?? ""
    }`;

    if (lastZoomedAgentRef.current === key) return;
    lastZoomedAgentRef.current = key;

    zoomToPoi(requestedTicketAgentPoi, zoomTo);
    setPoiInfoWindow({ type: "ticketAgent", poi: requestedTicketAgentPoi });
  }, [
    mapLoaded,
    requestedTicketAgentName,
    requestedTicketAgentLl,
    requestedTicketAgentPoi,
    mapRef,
    zoomToPoi,
    zoomTo,
  ]);

  // Öppna specifik automat från URL/state.
  React.useEffect(() => {
    if (!mapLoaded) return;
    if (
      (!requestedTicketMachineName && !requestedTicketMachineLl) ||
      !requestedTicketMachinePoi
    ) {
      return;
    }
    if (!mapRef.current) return;

    const key = `${requestedTicketMachineName ?? ""}|${
      requestedTicketMachineLl ?? ""
    }`;

    if (lastZoomedMachineRef.current === key) return;
    lastZoomedMachineRef.current = key;

    zoomToPoi(requestedTicketMachinePoi, zoomTo);
    setPoiInfoWindow({
      type: "ticketMachine",
      poi: requestedTicketMachinePoi,
    });
  }, [
    mapLoaded,
    requestedTicketMachineName,
    requestedTicketMachineLl,
    requestedTicketMachinePoi,
    mapRef,
    zoomToPoi,
    zoomTo,
  ]);

  const openParkingPoi = React.useCallback(
    (poi: Stop) => {
      setParkingEnabled(true);
      setPoiInfoWindow({ type: "parking", poi });
      setRequestedParkingName(poi.name);
      setRequestedParkingLl(formatPoiLl(poi));
      zoomToPoi(poi, zoomTo);
    },
    [
      setParkingEnabled,
      setRequestedParkingName,
      setRequestedParkingLl,
      zoomToPoi,
      zoomTo,
    ]
  );

  const openTicketAgentPoi = React.useCallback(
    (poi: Stop) => {
      setTicketAgentsEnabled(true);
      setPoiInfoWindow({ type: "ticketAgent", poi });
      setRequestedTicketAgentName(poi.name);
      setRequestedTicketAgentLl(formatPoiLl(poi));
      zoomToPoi(poi, zoomTo);
    },
    [
      setTicketAgentsEnabled,
      setRequestedTicketAgentName,
      setRequestedTicketAgentLl,
      zoomToPoi,
      zoomTo,
    ]
  );

  const openTicketMachinePoi = React.useCallback(
    (poi: Stop) => {
      setTicketMachinesEnabled(true);
      setPoiInfoWindow({ type: "ticketMachine", poi });
      setRequestedTicketMachineName(poi.name);
      setRequestedTicketMachineLl(formatPoiLl(poi));
      zoomToPoi(poi, zoomTo);
    },
    [
      setTicketMachinesEnabled,
      setRequestedTicketMachineName,
      setRequestedTicketMachineLl,
      zoomToPoi,
      zoomTo,
    ]
  );

  const closePoiPopup = React.useCallback(() => {
    if (!poiInfoWindow) return;

    const type = poiInfoWindow.type;

    setPoiInfoWindow(null);

    if (type === "parking") {
      lastZoomedParkingIdRef.current = null;
      setRequestedParkingName(null);
      setRequestedParkingLl(null);
    }

    if (type === "ticketAgent") {
      lastZoomedAgentRef.current = null;
      setRequestedTicketAgentName(null);
      setRequestedTicketAgentLl(null);
    }

    if (type === "ticketMachine") {
      lastZoomedMachineRef.current = null;
      setRequestedTicketMachineName(null);
      setRequestedTicketMachineLl(null);
    }
  }, [
    poiInfoWindow,
    setRequestedParkingName,
    setRequestedParkingLl,
    setRequestedTicketAgentName,
    setRequestedTicketAgentLl,
    setRequestedTicketMachineName,
    setRequestedTicketMachineLl,
  ]);

  return (
    <>
      {parkingEnabled &&
        parkingPois.map((m) => (
          <Marker
            key={`parking-${m.id}`}
            position={{ lat: m.lat, lng: m.lng }}
            title={m.name}
            icon={SvgMarkerIcon({
              topIconName: "p",
              color: "#0078C7",
              height: 38,
              showIcon: true,
            })}
            onClick={() => openParkingPoi(m)}
          />
        ))}

      {ticketAgentsEnabled &&
        ticketAgentsPois.map((m) => (
          <Marker
            key={`agent-${m.id}`}
            position={{ lat: m.lat, lng: m.lng }}
            title={m.name}
            icon={SvgMarkerIcon({
              topIconName: "vendor",
              badgeIconName: "vendor",
              height: 38,
              showIcon: true,
            })}
            onClick={() => openTicketAgentPoi(m)}
          />
        ))}

      {ticketMachinesEnabled &&
        ticketMachinesPois.map((m) => (
          <Marker
            key={`machine-${m.id}`}
            position={{ lat: m.lat, lng: m.lng }}
            title={m.name}
            icon={SvgMarkerIcon({
              topIconName: "Logomobile",
              badgeIconName: "Logomobile",
              height: 38,
              color: "#747474",
              showIcon: true,
            })}
            onClick={() => openTicketMachinePoi(m)}
          />
        ))}

      {poiInfoWindow && (
        <PoiInfoWindow
          type={poiInfoWindow.type}
          poi={poiInfoWindow.poi}
          position={{
            lat: poiInfoWindow.poi.lat,
            lng: poiInfoWindow.poi.lng,
          }}
          onClose={closePoiPopup}
        />
      )}
    </>
  );
}
